import { Inject, Injectable, NgZone } from '@angular/core';
import { Game } from '../interfaces/game.interface';
import { GameState } from '../enums/gamestate.enum';

import { HttpClient } from '@angular/common/http';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';

import { StorageConstants } from '../constants/storage.constants';
import { EventConstants } from '../constants/event.constants';
import { VoteProcessingService } from './vote-processing.service';
import { State } from '../interfaces/state.interface';
import { VoteMap } from '../interfaces/votemap.interface';
import { MemeTriggerService } from './meme-trigger.service';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  private hubConnection: HubConnection;
  private onDestroySource: Subject<void> = new Subject<void>();

  private stateUpdateProcessedSource: Subject<void> = new Subject<void>();
  private stateUpdateReceivedSource: Subject<State> = new Subject<State>();
  private votesMapSource: Subject<VoteMap> = new Subject<VoteMap>();
  onDestroy$: Observable<void> = this.onDestroySource.asObservable();
  stateUpdateReceived$: Observable<State> = this.stateUpdateReceivedSource.asObservable();
  stateUpdateProcessed$: Observable<void> = this.stateUpdateProcessedSource.asObservable();
  votesMapReceived$: Observable<VoteMap> = this.votesMapSource.asObservable();

  gameState: GameState;

  baseUrl: string;
  restUrl: string;
  hasVoted: boolean;
  isAlone: boolean;

  constructor(private zone: NgZone, private http: HttpClient, @Inject('BASE_URL') baseUrl: string, private voteProcessingService: VoteProcessingService, private memeService: MemeTriggerService)
  {
    this.baseUrl = baseUrl;
    this.restUrl = baseUrl + 'api/game/';
  }

  // POST: call back-end to create a new game
  startGame() {
    return this.http.post<Game>(this.restUrl + "creategame", '').toPromise();
  }

  // GET: Call back-end to receive a game
  getGame(gameID: string) {
    return this.http.get<Game>(this.restUrl + 'getgame/' + gameID).toPromise();
  }

  registerPlayer() {
    return this.http.post(this.restUrl + 'createPlayerID', {}).toPromise();
  }

  // POST: call back-end to join the game
  joinGame(playerName: string) {
    let gameID = sessionStorage.getItem(StorageConstants.GameID);
    let playerID = sessionStorage.getItem(StorageConstants.PlayerID);
    return this.http.post(this.restUrl + gameID + '/join/', { playerName: playerName, playerID: playerID }).toPromise();
  }

  // POST: call back-end to cast a vote
  castVote(votedCard: string) {
    this.hasVoted = votedCard !== null;
    let gameID = sessionStorage.getItem(StorageConstants.GameID);
    let playerID = sessionStorage.getItem(StorageConstants.PlayerID);
    return this.http.post(this.restUrl + gameID + '/vote/' + playerID, { card: votedCard }).toPromise();
  }

  // POST: call back-end to show all votes
  showVotes() {
    let gameID = sessionStorage.getItem(StorageConstants.GameID);
    return this.http.post(this.restUrl+ gameID + '/show', []).toPromise();
  }

  // POST: call back-end to start a new round
  startRound() {
    let gameID = sessionStorage.getItem(StorageConstants.GameID);
    return this.http.post(this.restUrl + gameID + '/start', []).toPromise();
  }

  // SignalR
  connect() {
    return this.hubConnection.invoke("Connect", sessionStorage.getItem(StorageConstants.GameID), sessionStorage.getItem(StorageConstants.PlayerID));
  }

  disconnect() {
    this.stop();
  }

  async reconnect(): Promise<void> {
    this.hubConnection.off(EventConstants.StateChanged);

    await this.start();
    await this.connect();
    await this.joinGame(localStorage.getItem(StorageConstants.PlayerName));
  }

  start(): Promise<void> {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.baseUrl + "gamehub")
      .build();

    this.hubConnection.onclose(async (error?: Error) => {
      if (error != null) {
        await this.reconnect();
        return;
      }

      this.stop();
    });

    this.hubConnection.on(EventConstants.StateChanged, (args) => {
      let state: State = JSON.parse(args);
      this.gameState = state.GameState;
      this.isAlone = state.Players.length == 1;

      state.VoteMap = this.voteProcessingService.processVotes(state);
      state.MemeState = this.memeService.checkMemes(state);

      this.stateUpdateReceivedSource.next(state);

      if (state.GameState == GameState.ShowVotes) {
        this.votesMapSource.next(state.VoteMap);
      }

      // Force re-render of the webpage.
      this.zone.run(() => { });
    });

    return this.hubConnection.start();
  }

  isConnected() {
    if (this.hubConnection == null) {
      return false;
    }

    return this.hubConnection.state == HubConnectionState.Connected;
  }

  private stop() {
    this.hubConnection.stop();
    this.hubConnection.off(EventConstants.StateChanged);
    this.onDestroySource.next();
  }
}
